// Crypto Wallets → Custody → Hot Liquidity (SM-279).
// Live per-asset treasury status (the low-liquidity alert), refill
// requests, and the approval workflow. Approving moves COLD funds, so it
// needs CRYPTO_WALLET_REFILL_APPROVE and — when the admin has an MFA
// factor enrolled — a fresh step-up verification.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TechHint } from "@/components/ui/TechHint";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import { errMsg, td, th } from "@/components/wallet-ops/configShared";
import { useNetworkMap, networkLabel } from "@/components/wallet-ops/orders";
import { useAdminMap, adminName } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";
import { formatTokenBase } from "@/lib/tokenFmt";
import {
  approveRefill,
  createRefill,
  fetchHotLiquidity,
  fetchRefills,
  refillStepUpStart,
  refillStepUpVerify,
  rejectRefill,
  walletKeys,
} from "@/lib/wallet-queries";
import type {
  CryptoHotLiquidityStatus,
  CryptoRefill,
  CryptoRefillStatus,
} from "@/lib/wallet-types";

export const Route = createFileRoute("/_app/wallet-ops/liquidity")({
  component: HotLiquidityPage,
});

const statusTone: Record<CryptoRefillStatus, Parameters<typeof Badge>[0]["tone"]> = {
  PENDING_APPROVAL: "warning",
  REJECTED: "muted",
  APPROVED: "accent",
  BROADCAST: "accent",
  CONFIRMING: "accent",
  DONE: "success",
  FAILED: "danger",
};

function HotLiquidityPage() {
  const perms = useMyPerms();
  const nets = useNetworkMap();
  const admins = useAdminMap();
  const canRequest =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CryptoWalletRefillRequest);
  const canApprove =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CryptoWalletRefillApprove);

  const status = useQuery({
    queryKey: walletKeys.hotLiquidity(),
    queryFn: fetchHotLiquidity,
    refetchInterval: 15_000,
  });
  const refills = useQuery({
    queryKey: walletKeys.refills(undefined, 20, 0),
    queryFn: () => fetchRefills(undefined, 20, 0),
    refetchInterval: 10_000,
  });

  const [requesting, setRequesting] = useState<CryptoHotLiquidityStatus | null>(null);
  const [approving, setApproving] = useState<CryptoRefill | null>(null);
  const [rejecting, setRejecting] = useState<CryptoRefill | null>(null);

  const rows = status.data ?? [];
  const alerts = rows.filter((r) => r.below_min);
  const byAsset = (id: string) => rows.find((r) => r.asset_id === id);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-t-1">Hot liquidity</h1>
        <p className="mt-1 max-w-3xl text-sm text-t-2">
          Live hot-wallet balances against each asset's liquidity policy.
          When a balance is below its minimum, withdrawals are at risk:
          request a COLD → HOT refill here — a second operator approves it
          before anything moves.
        </p>
      </div>

      {alerts.length > 0 && (
        <div className="rounded-lg border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-t-1">
          <b>
            {alerts.length} hot wallet{alerts.length > 1 ? "s" : ""} below
            minimum:
          </b>{" "}
          {alerts.map((a) => a.symbol).join(", ")} — withdrawals for{" "}
          {alerts.length > 1 ? "these assets" : "this asset"} can start
          failing once the float runs out.
        </div>
      )}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Treasury status</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {status.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              className="m-5"
              title="No hot liquidity policies"
              description="Create one under Configuration → Hot Liquidity Policies to get alerts and refills for an asset."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={th}>Asset</th>
                    <th className={th}>Hot balance</th>
                    <th className={th}>Policy</th>
                    <th className={th}>Suggested refill</th>
                    <th className={th}>Active request</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const dec = r.token_decimals;
                    const suggested = r.suggested_base && r.suggested_base !== "0";
                    return (
                      <tr key={r.asset_id} className="border-b border-border/60 last:border-0">
                        <td className={td}>
                          <div className="font-medium text-t-1">{r.symbol}</div>
                          <div className="text-2xs text-muted" title={r.network_id}>
                            {networkLabel(nets, r.network_id)}
                          </div>
                        </td>
                        <td className={td}>
                          {r.balance_error ? (
                            <span className="text-2xs text-danger" title={r.balance_error}>
                              unreadable
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-t-1">
                                {formatTokenBase(r.balance_base, dec)} {r.symbol}
                              </span>
                              {r.below_min && <Badge tone="danger">below min</Badge>}
                            </div>
                          )}
                          <TechHint tech={r.hot_address} className="text-2xs">
                            <span className="font-mono text-muted">{r.hot_address.slice(0, 10)}…</span>
                          </TechHint>
                        </td>
                        <td className={`${td} text-t-2`}>
                          min {formatTokenBase(r.min_base || "0", dec)} · target{" "}
                          {formatTokenBase(r.target_base || "0", dec)} ·{" "}
                          {r.max_base ? `max ${formatTokenBase(r.max_base, dec)}` : "never swept"}
                        </td>
                        <td className={td}>
                          {suggested ? (
                            <span className="text-t-1">
                              {formatTokenBase(r.suggested_base, dec)} {r.symbol}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className={td}>
                          {r.active_refill ? (
                            <Badge tone={statusTone[r.active_refill.status] ?? "default"}>
                              {r.active_refill.status}
                            </Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className={`${td} text-right`}>
                          {canRequest && (
                            <Button
                              size="sm"
                              disabled={!suggested || !!r.active_refill || !!r.balance_error}
                              onClick={() => setRequesting(r)}
                            >
                              Request refill
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Refill requests</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {refills.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-10" />
            </div>
          ) : (refills.data?.refills ?? []).length === 0 ? (
            <EmptyState
              className="m-5"
              title="No refill requests yet"
              description="Requests appear here for a second operator to approve or reject."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={th}>Requested</th>
                    <th className={th}>Asset</th>
                    <th className={th}>Amount</th>
                    <th className={th}>Status</th>
                    <th className={th}>People</th>
                    <th className={th}>Result</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {(refills.data?.refills ?? []).map((f) => {
                    const dec = byAsset(f.asset_id)?.token_decimals ?? 18;
                    const symbol = byAsset(f.asset_id)?.symbol ?? f.asset_id;
                    return (
                      <tr key={f.refill_id} className="border-b border-border/60 last:border-0">
                        <td className={`${td} text-muted`}>{fmtRelative(f.created_at)}</td>
                        <td className={td}>
                          <div className="font-medium text-t-1">{symbol}</div>
                          <div className="text-2xs text-muted" title={f.network_id}>
                            {networkLabel(nets, f.network_id)}
                          </div>
                        </td>
                        <td className={td}>
                          <span className="text-t-1">
                            {formatTokenBase(f.amount_base, dec)} {symbol}
                          </span>
                          <div className="text-2xs text-muted">
                            hot held {formatTokenBase(f.hot_balance_base, dec)} at request
                          </div>
                        </td>
                        <td className={td}>
                          <Badge tone={statusTone[f.status] ?? "default"}>{f.status}</Badge>
                        </td>
                        <td className={`${td} text-2xs text-t-2`}>
                          <div title={f.requested_by}>req: {adminName(admins, f.requested_by)}</div>
                          <div title={f.decided_by}>dec: {adminName(admins, f.decided_by)}</div>
                        </td>
                        <td className={`${td} max-w-[220px]`}>
                          {f.tx_hash ? (
                            <TechHint tech={f.tx_hash} className="text-2xs">
                              <span className="font-mono text-muted">{f.tx_hash.slice(0, 12)}…</span>
                            </TechHint>
                          ) : f.last_error ? (
                            <span className="block truncate text-2xs text-danger" title={f.last_error}>
                              {f.last_error}
                            </span>
                          ) : f.decision_reason ? (
                            <span className="text-2xs text-muted">{f.decision_reason}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className={`${td} text-right`}>
                          {canApprove && f.status === "PENDING_APPROVAL" && (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" onClick={() => setApproving(f)}>
                                Approve
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => setRejecting(f)}>
                                Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <RequestRefillModal
        row={requesting}
        onClose={() => setRequesting(null)}
      />
      <ApproveRefillModal
        refill={approving}
        row={approving ? byAsset(approving.asset_id) : undefined}
        onClose={() => setApproving(null)}
      />
      <RejectRefillModal refill={rejecting} onClose={() => setRejecting(null)} />
    </div>
  );
}

function useRefillInvalidate() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: walletKeys.refillsAll() });
    await qc.invalidateQueries({ queryKey: walletKeys.hotLiquidity() });
  };
}

function RequestRefillModal({
  row,
  onClose,
}: {
  row: CryptoHotLiquidityStatus | null;
  onClose: () => void;
}) {
  const invalidate = useRefillInvalidate();
  const [amount, setAmount] = useState("");
  const [lastAsset, setLastAsset] = useState("");
  if (row && row.asset_id !== lastAsset) {
    setLastAsset(row.asset_id);
    setAmount(row.suggested_base);
  }
  const save = useMutation({
    mutationFn: () => createRefill({ asset_id: row!.asset_id, amount_base: amount }),
    onSuccess: async () => {
      onClose();
      setLastAsset("");
      await invalidate();
    },
    meta: { successMessage: "Refill requested — awaiting approval." },
  });
  const dec = row?.token_decimals ?? 18;
  return (
    <FormModal
      open={row !== null}
      title={`Request COLD → HOT refill — ${row?.symbol ?? ""}`}
      description="Nothing moves yet: the request waits for an approver, and the transfer only executes after approval."
      confirmLabel="Request refill"
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={amount !== "" && amount !== "0"}
      onSubmit={() => save.mutate()}
      onCancel={() => {
        onClose();
        setLastAsset("");
        save.reset();
      }}
    >
      <div className="space-y-4">
        <div className="rounded-md bg-surface-2 p-3 text-2xs text-t-2">
          <div>
            hot {row?.hot_address} holds{" "}
            {row ? formatTokenBase(row.balance_base, dec) : ""} {row?.symbol}
          </div>
          <div>
            target {row ? formatTokenBase(row.target_base || "0", dec) : ""} · est. network fee{" "}
            {row?.fee_estimate ? formatTokenBase(row.fee_estimate, 18) : "?"} (native)
          </div>
        </div>
        <TokenAmountInput
          label="Amount"
          symbol={row?.symbol}
          base={amount}
          decimals={dec}
          onBase={setAmount}
          allowEmpty
          emptyHint="Defaults to target − current balance."
        />
      </div>
    </FormModal>
  );
}

function ApproveRefillModal({
  refill,
  row,
  onClose,
}: {
  refill: CryptoRefill | null;
  row: CryptoHotLiquidityStatus | undefined;
  onClose: () => void;
}) {
  const invalidate = useRefillInvalidate();
  const admins = useAdminMap();
  const [stepUp, setStepUp] = useState<{
    checked: boolean;
    required: boolean;
    challengeID: string;
    factors: { factor_id: string; kind: string; friendly_name: string }[];
    factorID: string;
    code: string;
    token: string;
    error: string;
  }>({ checked: false, required: false, challengeID: "", factors: [], factorID: "", code: "", token: "", error: "" });
  const [lastID, setLastID] = useState("");

  // On open: find out whether this admin needs a step-up verification.
  if (refill && refill.refill_id !== lastID) {
    setLastID(refill.refill_id);
    setStepUp((s) => ({ ...s, checked: false, token: "", code: "", error: "" }));
    void refillStepUpStart()
      .then((r) =>
        setStepUp({
          checked: true,
          required: r.mfa_required,
          challengeID: r.challenge_id ?? "",
          factors: r.factors ?? [],
          factorID: r.factors?.[0]?.factor_id ?? "",
          code: "",
          token: "",
          error: "",
        }),
      )
      .catch((e) =>
        setStepUp((s) => ({ ...s, checked: true, required: true, error: errMsg(e) })),
      );
  }

  const verify = useMutation({
    mutationFn: () =>
      refillStepUpVerify({
        challenge_id: stepUp.challengeID,
        factor_id: stepUp.factorID,
        code: stepUp.code,
      }),
    onSuccess: (r) => setStepUp((s) => ({ ...s, token: r.stepup_token, error: "" })),
    onError: (e) => setStepUp((s) => ({ ...s, error: errMsg(e) })),
  });

  const approve = useMutation({
    mutationFn: () => approveRefill(refill!.refill_id, stepUp.token),
    onSuccess: async () => {
      onClose();
      setLastID("");
      await invalidate();
    },
    meta: { successMessage: "Refill approved — executing COLD → HOT transfer." },
  });

  const dec = row?.token_decimals ?? 18;
  const symbol = row?.symbol ?? refill?.asset_id ?? "";
  const stepUpSatisfied = stepUp.checked && (!stepUp.required || stepUp.token !== "");
  return (
    <FormModal
      open={refill !== null}
      title={`Approve refill — ${symbol}`}
      description="Approving executes the transfer from the COLD wallet through the custody signer. This cannot be undone once broadcast."
      confirmLabel="Approve & execute"
      loading={approve.isPending}
      error={approve.isError ? errMsg(approve.error) : null}
      canSubmit={stepUpSatisfied}
      onSubmit={() => approve.mutate()}
      onCancel={() => {
        onClose();
        setLastID("");
        approve.reset();
      }}
    >
      <div className="space-y-4">
        <div className="rounded-md bg-surface-2 p-3 text-2xs text-t-2">
          <div>
            <b className="text-t-1">
              {refill ? formatTokenBase(refill.amount_base, dec) : ""} {symbol}
            </b>{" "}
            · est. fee {refill?.fee_estimate_base ? formatTokenBase(refill.fee_estimate_base, 18) : "?"}{" "}
            (native)
          </div>
          <div className="mt-1 font-mono">from COLD {refill?.cold_address}</div>
          <div className="font-mono">to HOT {refill?.hot_address}</div>
          <div className="mt-1" title={refill?.requested_by ?? undefined}>
            requested by {adminName(admins, refill?.requested_by)}
          </div>
        </div>

        {!stepUp.checked ? (
          <Skeleton className="h-10" />
        ) : stepUp.required && !stepUp.token ? (
          <div className="space-y-3 rounded-md border border-border p-3">
            <p className="text-2xs text-t-2">
              Step-up verification required: confirm your second factor to
              unlock approval.
            </p>
            {stepUp.factors.length > 1 && (
              <Select
                label="Factor"
                value={stepUp.factorID}
                onChange={(e) =>
                  setStepUp((s) => ({ ...s, factorID: e.target.value }))
                }
                options={stepUp.factors.map((f) => ({
                  value: f.factor_id,
                  label: f.friendly_name || f.kind,
                }))}
              />
            )}
            <div className="flex items-end gap-2">
              <Input
                label="Authentication code"
                value={stepUp.code}
                onChange={(e) => setStepUp((s) => ({ ...s, code: e.target.value }))}
                placeholder="123456"
              />
              <Button
                size="sm"
                disabled={stepUp.code === "" || verify.isPending}
                onClick={() => verify.mutate()}
              >
                Verify
              </Button>
            </div>
            {stepUp.error && <p className="text-2xs text-danger">{stepUp.error}</p>}
          </div>
        ) : stepUp.required ? (
          <p className="text-2xs text-success">Second factor verified.</p>
        ) : (
          <p className="text-2xs text-muted">
            No second factor enrolled — approval proceeds without step-up.
          </p>
        )}
      </div>
    </FormModal>
  );
}

function RejectRefillModal({
  refill,
  onClose,
}: {
  refill: CryptoRefill | null;
  onClose: () => void;
}) {
  const invalidate = useRefillInvalidate();
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () => rejectRefill(refill!.refill_id, reason),
    onSuccess: async () => {
      onClose();
      setReason("");
      await invalidate();
    },
    meta: { successMessage: "Refill rejected." },
  });
  return (
    <FormModal
      open={refill !== null}
      title="Reject refill request"
      description="The request closes without moving anything; a new one can be raised any time."
      confirmLabel="Reject"
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={reason.trim() !== ""}
      onSubmit={() => save.mutate()}
      onCancel={() => {
        onClose();
        setReason("");
        save.reset();
      }}
    >
      <Input
        label="Reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why this refill isn't needed"
        hint="Recorded on the request and in the audit log."
      />
    </FormModal>
  );
}
